/**
 * **„Kalkulator"** — the target sheet's calculator (nutrition R19). The server only supplies the
 * inputs; every formula, factor and rounding lives here, and the result only fills the form.
 *
 * Resting energy: Mifflin-St Jeor (default), Katch-McArdle (needs body fat), Harris-Benedict
 * (Roza-Shizgal revision). Daily energy = resting × activity factor × (1 + goal %), rounded to
 * 10 kcal. Protein and fat are grams per kilogram of body weight; carbohydrate takes the energy left
 * at 4/4/9 and never goes below zero. Sex of Other or none is chosen by the trainer (R19).
 *
 * The activity factor, goal %, protein and fat are free numbers inside their ranges. The intake's
 * activity level only sets the starting factor; any factor is named by the level whose band it is in.
 */

import { macroKcal, type TargetFormValues } from './nutritionTarget';

export type CalcSex = 'female' | 'male';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'high' | 'veryHigh';
export type CalcFormula = 'mifflin' | 'katch' | 'harris';
/** What the trainer may type in for one calculation when the profile lacks it. */
export type CalcInput = 'sex' | 'age' | 'height' | 'weight';
export type TypedInput = 'sex' | 'age' | 'height' | 'weight' | 'bodyFat';

/** A free choice's bounds, the step of its ± control and the decimals a value keeps. */
export interface ChoiceRange {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly decimals: number;
}

export const ACTIVITY_FACTOR: Readonly<Record<ActivityLevel, number>> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
  veryHigh: 1.9,
};
export const ACTIVITY_FACTOR_RANGE: ChoiceRange = { min: 1.1, max: 2.2, step: 0.025, decimals: 3 };
/** The starting factor when the intake has no activity level. */
export const DEFAULT_ACTIVITY_FACTOR = ACTIVITY_FACTOR.moderate;

/** Each band's exclusive upper bound, the midpoint between two levels' factors; above the last is `veryHigh`. */
const ACTIVITY_BANDS: readonly (readonly [number, ActivityLevel])[] = [
  [1.2875, 'sedentary'],
  [1.4625, 'light'],
  [1.6375, 'moderate'],
  [1.8125, 'high'],
];

/** The level whose band a factor is in, which names it. */
export function activityBand(factor: number): ActivityLevel {
  return ACTIVITY_BANDS.find(([upper]) => factor < upper)?.[1] ?? 'veryHigh';
}

/** The factor the calculator starts from: the intake's level, else the default. */
export function initialActivityFactor(profile: CalculatorProfile): number {
  return profile.activity === null ? DEFAULT_ACTIVITY_FACTOR : ACTIVITY_FACTOR[profile.activity];
}

export type GoalDirection = 'reduction' | 'maintenance' | 'surplus';

/** The goal as a correction of the with-activity energy in whole percent; 0 is maintenance. */
export function goalDirection(percent: number): GoalDirection {
  if (percent < 0) return 'reduction';
  return percent > 0 ? 'surplus' : 'maintenance';
}

export const GOAL_PERCENT_RANGE: ChoiceRange = { min: -30, max: 20, step: 1, decimals: 0 };
export const DEFAULT_GOAL_PERCENT = 0;

export const CALC_FORMULAS: readonly CalcFormula[] = ['mifflin', 'katch', 'harris'];

export const PROTEIN_PER_KG_RANGE: ChoiceRange = { min: 1.2, max: 3, step: 0.1, decimals: 1 };
export const DEFAULT_PROTEIN_PER_KG = 2;

export const FAT_PER_KG_RANGE: ChoiceRange = { min: 0.5, max: 1.5, step: 0.1, decimals: 1 };
export const DEFAULT_FAT_PER_KG = 0.9;

/**
 * Half away from zero at `decimals` places. The scaled value is cut to 12 significant digits first,
 * so a typed 1,45 rounds up although its binary value is 1,4499….
 */
export function roundHalfAway(value: number, decimals: number): number {
  const scale = 10 ** decimals;
  const scaled = Number((Math.abs(value) * scale).toPrecision(12));
  return (Math.sign(value) * Math.round(scaled)) / scale + 0;
}

/** The value clamped into the range and rounded to its decimals; never snapped to the step grid. */
export function clampToRange(value: number, range: ChoiceRange): number {
  return roundHalfAway(Math.min(range.max, Math.max(range.min, value)), range.decimals);
}

/** A typed choice as a number: comma or dot, any minus sign, spaces ignored; anything else is `null`. */
export function parseChoice(text: string): number | null {
  const compact = text.replace(/\s/g, '').replace(/[\u2212\u2013]/g, '-').replace(',', '.');
  return /^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(compact) ? Number(compact) : null;
}

/** A typed choice on commit (blur, Enter, ±): clamped and rounded, or `null` to put the last value back. */
export function commitChoice(text: string, range: ChoiceRange): number | null {
  const value = parseChoice(text);
  return value === null ? null : clampToRange(value, range);
}

/** A choice still being typed counts only once it is inside the range; nothing is clamped meanwhile. */
export function typingChoice(text: string, range: ChoiceRange): number | null {
  const value = parseChoice(text);
  return value === null || value < range.min || value > range.max ? null : roundHalfAway(value, range.decimals);
}

/** Plausible bounds for a value typed only for this calculation; outside them it does not count. */
export const TYPED_BOUNDS: Readonly<Record<'age' | 'height' | 'weight' | 'bodyFat', readonly [number, number]>> = {
  age: [14, 100],
  height: [100, 250],
  weight: [30, 300],
  bodyFat: [3, 60],
};

/** What the server knows about the client (`calculator-inputs`). */
export interface CalculatorProfile {
  /** `'other'` is returned as recorded; the formulas need the trainer to choose. */
  readonly sex: CalcSex | 'other' | null;
  readonly dateOfBirth: string | null;
  readonly ageYears: number | null;
  readonly heightCm: number | null;
  readonly weight: {
    readonly kg: number;
    readonly bodyFatPercent: number | null;
    readonly measuredAt: string;
    readonly isStale: boolean;
  } | null;
  readonly activity: ActivityLevel | null;
}

/** Values the trainer typed for this calculation only; never saved to the profile. */
export interface CalculatorTyped {
  readonly sex: CalcSex | null;
  readonly age: string;
  readonly height: string;
  readonly weight: string;
  readonly bodyFat: string;
}

export interface CalculatorChoices {
  readonly formula: CalcFormula;
  /** `null` is the starting factor, `initialActivityFactor`. */
  readonly activityFactor: number | null;
  readonly goalPercent: number;
  readonly proteinPerKg: number;
  readonly fatPerKg: number;
}

export const EMPTY_TYPED: CalculatorTyped = { sex: null, age: '', height: '', weight: '', bodyFat: '' };
export const INITIAL_CHOICES: CalculatorChoices = {
  formula: 'mifflin',
  activityFactor: null,
  goalPercent: DEFAULT_GOAL_PERCENT,
  proteinPerKg: DEFAULT_PROTEIN_PER_KG,
  fatPerKg: DEFAULT_FAT_PER_KG,
};

export interface CalculatorBody {
  readonly sex: CalcSex | null;
  readonly ageYears: number | null;
  readonly heightCm: number | null;
  readonly weightKg: number | null;
  readonly bodyFatPercent: number | null;
}

/** `''` or anything that is not a positive number inside `bounds` is no value; a comma is a decimal point. */
export function parseTyped(text: string, bounds: readonly [number, number]): number | null {
  const compact = text.replace(/[\s\u00a0]/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(compact)) return null;
  const value = Number(compact);
  return value >= bounds[0] && value <= bounds[1] ? value : null;
}

/** Which of the profile's inputs the trainer has to type in; body fat only unlocks Katch-McArdle. */
export function typedInputs(profile: CalculatorProfile): ReadonlySet<TypedInput> {
  const inputs = new Set<TypedInput>();
  if (profile.sex !== 'female' && profile.sex !== 'male') inputs.add('sex');
  if (profile.ageYears === null) inputs.add('age');
  if (profile.heightCm === null) inputs.add('height');
  if (profile.weight === null) inputs.add('weight');
  if (profile.weight?.bodyFatPercent == null) inputs.add('bodyFat');
  return inputs;
}

/** The order the inputs appear in, which is the order a failed save looks for the first one. */
export const TYPED_INPUT_ORDER: readonly TypedInput[] = ['sex', 'age', 'height', 'weight', 'bodyFat'];

/** `missing`: a required input left empty; `invalid`: a typed value that is not a number inside its bounds. */
export type InputIssue = 'missing' | 'invalid';

/**
 * What is wrong with each input the trainer has to supply. Required are the inputs the formula in
 * effect needs (Katch-McArdle: weight only); body fat is optional, and a bad typed value is always wrong.
 */
export function inputIssues(
  profile: CalculatorProfile,
  typed: CalculatorTyped,
  formula: CalcFormula,
): Readonly<Partial<Record<TypedInput, InputIssue>>> {
  const required = requiredInputs(profile, typed, formula);
  const issues: Partial<Record<TypedInput, InputIssue>> = {};
  const inputs = typedInputs(profile);
  if (inputs.has('sex') && required.has('sex')) issues.sex = 'missing';
  for (const input of ['age', 'height', 'weight', 'bodyFat'] as const) {
    if (!inputs.has(input)) continue;
    const text = typed[input];
    if (text.trim() === '') {
      if (required.has(input)) issues[input] = 'missing';
    } else if (parseTyped(text, TYPED_BOUNDS[input]) === null) {
      issues[input] = 'invalid';
    }
  }
  return issues;
}

/** The inputs the formula in effect still lacks a value for, empty or not a number inside its bounds. */
export function requiredInputs(
  profile: CalculatorProfile,
  typed: CalculatorTyped,
  formula: CalcFormula,
): ReadonlySet<TypedInput> {
  const body = resolveBody(profile, typed);
  return new Set(missingFor(effectiveFormula(formula, body), body));
}

/** Katch-McArdle is offered only with body fat; a stale choice falls back to the default. */
function effectiveFormula(formula: CalcFormula, body: CalculatorBody): CalcFormula {
  return formula === 'katch' && body.bodyFatPercent === null ? 'mifflin' : formula;
}

export function resolveBody(profile: CalculatorProfile, typed: CalculatorTyped): CalculatorBody {
  return {
    sex: profile.sex === 'female' || profile.sex === 'male' ? profile.sex : typed.sex,
    ageYears: profile.ageYears ?? parseTyped(typed.age, TYPED_BOUNDS.age),
    heightCm: profile.heightCm ?? parseTyped(typed.height, TYPED_BOUNDS.height),
    weightKg: profile.weight?.kg ?? parseTyped(typed.weight, TYPED_BOUNDS.weight),
    bodyFatPercent: profile.weight?.bodyFatPercent ?? parseTyped(typed.bodyFat, TYPED_BOUNDS.bodyFat),
  };
}

/** Resting energy in kcal, unrounded; `null` when the formula lacks an input. */
export function restingEnergy(formula: CalcFormula, body: CalculatorBody): number | null {
  const { sex, ageYears: age, heightCm: height, weightKg: weight, bodyFatPercent: fat } = body;
  if (formula === 'katch') {
    if (weight === null || fat === null) return null;
    return 370 + 21.6 * weight * (1 - fat / 100);
  }
  if (sex === null || age === null || height === null || weight === null) return null;
  if (formula === 'harris') {
    return sex === 'female'
      ? 447.593 + 9.247 * weight + 3.098 * height - 4.33 * age
      : 88.362 + 13.397 * weight + 4.799 * height - 5.677 * age;
  }
  return 10 * weight + 6.25 * height - 5 * age + (sex === 'female' ? -161 : 5);
}

export interface CalculatorResult {
  /** Resting and with-activity energy, rounded to whole kcal for display. */
  readonly resting: number;
  readonly withActivity: number;
  /** The choices as calculated, clamped to their ranges. */
  readonly factor: number;
  readonly goalPercent: number;
  readonly proteinPerKg: number;
  readonly fatPerKg: number;
  /** What the goal adds to the with-activity energy, rounded to 10 kcal. */
  readonly goalKcal: number;
  readonly energy: number;
  readonly protein: number;
  readonly carbs: number;
  readonly fat: number;
  /** Protein and fat alone exceed the energy, so carbohydrate is held at zero. */
  readonly carbsShort: boolean;
}

export type CalculatorOutcome =
  | { readonly kind: 'ready'; readonly result: CalculatorResult }
  | { readonly kind: 'incomplete'; readonly missing: readonly CalcInput[] };

function missingFor(formula: CalcFormula, body: CalculatorBody): CalcInput[] {
  const missing: CalcInput[] = [];
  if (formula !== 'katch') {
    if (body.sex === null) missing.push('sex');
    if (body.ageYears === null) missing.push('age');
    if (body.heightCm === null) missing.push('height');
  }
  if (body.weightKg === null) missing.push('weight');
  return missing;
}

export function calculateTarget(
  body: CalculatorBody,
  formula: CalcFormula,
  activityFactor: number,
  goalPercent: number,
  proteinPerKg: number,
  fatPerKg: number = DEFAULT_FAT_PER_KG,
): CalculatorOutcome {
  const resting = restingEnergy(formula, body);
  if (resting === null || body.weightKg === null) {
    return { kind: 'incomplete', missing: missingFor(formula, body) };
  }
  const factor = clampToRange(activityFactor, ACTIVITY_FACTOR_RANGE);
  const goal = clampToRange(goalPercent, GOAL_PERCENT_RANGE);
  const proteinRate = clampToRange(proteinPerKg, PROTEIN_PER_KG_RANGE);
  const fatRate = clampToRange(fatPerKg, FAT_PER_KG_RANGE);
  const withActivity = resting * factor;
  const energy = Math.round((withActivity * (1 + goal / 100)) / 10) * 10;
  const protein = Math.round(body.weightKg * proteinRate);
  const fat = Math.round(body.weightKg * fatRate);
  const left = energy - macroKcal(protein, 0, fat);
  return {
    kind: 'ready',
    result: {
      resting: Math.round(resting),
      withActivity: Math.round(withActivity),
      factor,
      goalPercent: goal,
      proteinPerKg: proteinRate,
      fatPerKg: fatRate,
      goalKcal: Math.round((withActivity * goal) / 1000) * 10 + 0,
      energy,
      protein,
      carbs: Math.max(0, Math.round(left / 4)),
      fat,
      carbsShort: left < 0,
    },
  };
}

/** The calculator over the profile, the typed values and the trainer's choices. */
export function calculatorOutcome(
  profile: CalculatorProfile,
  typed: CalculatorTyped,
  choices: CalculatorChoices,
): CalculatorOutcome {
  const body = resolveBody(profile, typed);
  const formula = effectiveFormula(choices.formula, body);
  const factor = choices.activityFactor ?? initialActivityFactor(profile);
  return calculateTarget(body, formula, factor, choices.goalPercent, choices.proteinPerKg, choices.fatPerKg);
}

export interface FormulaPick {
  readonly choices: CalculatorChoices;
  readonly awaitingBodyFat: boolean;
}

/** A tapped formula; Katch-McArdle without body fat is not picked but waits for the body fat. */
export function tapFormula(
  choices: CalculatorChoices,
  formula: CalcFormula,
  bodyFatPercent: number | null,
): FormulaPick {
  if (formula === 'katch' && bodyFatPercent === null) return { choices, awaitingBodyFat: true };
  return { choices: choices.formula === formula ? choices : { ...choices, formula }, awaitingBodyFat: false };
}

/** A typed body fat picks the awaited Katch-McArdle once the value counts. */
export function typeBodyFat(choices: CalculatorChoices, awaitingBodyFat: boolean, text: string): FormulaPick {
  if (!awaitingBodyFat || parseTyped(text, TYPED_BOUNDS.bodyFat) === null) return { choices, awaitingBodyFat };
  return { choices: { ...choices, formula: 'katch' }, awaitingBodyFat: false };
}

/** The result as the hand-typed form holds it: energy typed explicitly, so it is saved as computed. */
export function formFromResult(result: CalculatorResult): TargetFormValues {
  return {
    energy: String(result.energy),
    protein: String(result.protein),
    carbs: String(result.carbs),
    fat: String(result.fat),
  };
}
